/**
 * BPF-owned Memorial submission form — Phase 4D.7A.
 *
 * Progressively enhances the native <form> in
 * sections/bpf-memorial-submission-form.liquid: the form is a structurally
 * valid, plain multipart/form-data POST to /apps/memorial on its own
 * (Shopify's App Proxy, not the Cloud Run URL directly), so a
 * non-JS/failed-script visit still works exactly like an ordinary form
 * submission. This file only adds a fetch-based submit so the customer
 * gets inline success/error UX instead of a full page navigation.
 *
 * Deliberately does NOT construct or sign any App Proxy query parameters,
 * does NOT set a Content-Type header (FormData lets the browser generate
 * the correct multipart boundary itself), and never references any
 * Shopify credential — Shopify signs the proxied request server-side,
 * entirely outside this script's reach.
 *
 * Phase 4D.7A.3 — reveal-on-demand: the section renders `hidden` by
 * default (see the Liquid file) and is only revealed when a same-page
 * link targeting it is followed. This is deliberately generic: it never
 * looks for the Memorial Wall CTA, Globo, or any specific trigger
 * element by name — any link on the page that happens to point at this
 * element will open it, keeping the two systems decoupled.
 *
 * Phase 4D.7A.9 — the owned form's wrapper now uses a fixed, hand-
 * authored id ("bpf-memorial-submission-form", set in the Liquid file)
 * instead of Shopify's auto-generated `shopify-section-{{ section.id }}`
 * wrapper id: live testing proved that generated id actually renders
 * with an unpredictable per-template-instance prefix, so no
 * `#shopify-section-<id>`-based anchor could ever resolve correctly.
 * Anchor matching/resolution below no longer assumes any particular
 * href prefix — it inspects every same-page anchor and keeps only the
 * ones that actually resolve to an owned form element.
 *
 * Phase 4D.7A.5 — Theme Editor hardening: `initForm()` is re-run on
 * Shopify Theme Editor's `shopify:section:load`/`shopify:section:select`
 * events — Theme Editor can re-render a section's markup via its Section
 * Rendering API without a full page reload, which would otherwise leave
 * a freshly-inserted <form> with no listeners attached (initForm() is
 * idempotent per node via a data-attribute guard, so this is safe to
 * call repeatedly).
 *
 * Phase 4D.7A.7 — the reveal trigger was originally a single
 * document-level delegated click listener matching any
 * `a[href^="#shopify-section-"]` at click time. That passed local
 * simulation but did not fire in live Shopify Theme Editor testing, so
 * it's replaced here with direct listeners bound to the specific
 * anchor(s) that actually target an owned BPF Memorial Submission form
 * (bindRevealTriggers()) — simpler to reason about, and each anchor's
 * target is still re-resolved fresh at click time (not cached from bind
 * time), so it stays correct even if Theme Editor later re-renders the
 * owned form section on its own.
 */
(function () {
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024; // matches the backend's MAX_IMAGE_BYTES
  var ACCEPTED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

  function onReady() {
    initAllForms();
    bindRevealTriggers(document);
    revealFromInitialHash();

    document.addEventListener("shopify:section:load", function (event) {
      var scope = event.target || document;
      scope.querySelectorAll("[data-bpf-memorial-submission-form]").forEach(initForm);
      // Always re-scanned from `document`, not just the reloaded scope —
      // Theme Editor may have re-rendered the Memorial Wall section (new
      // CTA anchor) or the owned form section (new target element)
      // independently of one another.
      bindRevealTriggers(document);
    });
    document.addEventListener("shopify:section:select", function (event) {
      var scope = event.target || document;
      scope.querySelectorAll("[data-bpf-memorial-submission-form]").forEach(initForm);
      bindRevealTriggers(document);
    });
  }

  function initAllForms() {
    document.querySelectorAll("[data-bpf-memorial-submission-form]").forEach(initForm);
  }

  // --- reveal-on-demand -----------------------------------------------

  /**
   * Binds a direct click listener to every same-page anchor under `root`
   * whose href resolves to an owned BPF Memorial Submission form —
   * never Globo, never any other section, and never hardcoded to a
   * specific known id (works whether the anchor points at the form's
   * own stable id directly, or — defensively — at a wrapper containing
   * it). Anchors that don't resolve to an owned form are left completely
   * untouched. Idempotent per anchor via data-bpf-memorial-reveal-bound.
   */
  function bindRevealTriggers(root) {
    root = root || document;
    root.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
      if (anchor.dataset.bpfMemorialRevealBound === "true") return;

      var href = anchor.getAttribute("href");
      var target = resolveOwnedFormTarget(href);
      if (!target) return; // not an owned-form-targeting anchor — leave alone

      anchor.dataset.bpfMemorialRevealBound = "true";
      if (target.id) {
        anchor.setAttribute("aria-controls", target.id);
      }
      anchor.setAttribute("aria-expanded", target.hidden ? "false" : "true");

      anchor.addEventListener("click", function (event) {
        // Re-resolved fresh rather than reusing the `target` captured
        // above, so a later re-render of just the owned form section
        // (leaving this same anchor in place) can't leave this listener
        // pointing at a detached/stale element.
        var currentTarget = resolveOwnedFormTarget(href) || target;
        event.preventDefault();
        revealForm(currentTarget);
        anchor.setAttribute("aria-expanded", "true");
      });
    });
  }

  // A visitor may land directly on a URL that already carries a matching
  // hash (e.g. a shared/reloaded link) — native fragment scrolling
  // silently does nothing useful against a `hidden` element, so reveal
  // explicitly in that case too.
  function revealFromInitialHash() {
    if (!window.location.hash) return;
    var initialTarget = resolveOwnedFormTarget(window.location.hash);
    if (initialTarget) {
      revealForm(initialTarget);
    }
  }

  function resolveOwnedFormTarget(hash) {
    if (!hash || hash.charAt(0) !== "#") return null;
    var el = document.getElementById(hash.slice(1));
    if (!el) return null;
    // Primary case: the anchor points directly at the owned form's own
    // stable id, which itself carries the marker attribute. Defensive
    // fallback: an anchor pointing at some wrapper that merely contains
    // the owned form (kept in case that ever legitimately occurs).
    if (el.hasAttribute("data-bpf-memorial-submission-form-section")) return el;
    return el.querySelector("[data-bpf-memorial-submission-form-section]");
  }

  function revealForm(target) {
    if (!target.hidden) {
      target.focus();
      return;
    }
    target.hidden = false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    target.focus({ preventScroll: true });
  }

  function initForm(form) {
    // Idempotency guard: Theme Editor may call this again for the same
    // node (or a section may re-fire its load event without an actual
    // DOM swap) — never attach a second set of listeners to one form.
    if (form.dataset.bpfMemorialInitialized === "true") return;
    form.dataset.bpfMemorialInitialized = "true";

    var section = form.closest("[data-bpf-memorial-submission-form-section]");
    var submitButton = form.querySelector("[data-bpf-memorial-submit]");
    var submitLabelEl = form.querySelector("[data-bpf-memorial-submit-label]");
    var formError = form.querySelector("[data-bpf-memorial-form-error]");
    var photoInput = form.querySelector("[data-bpf-memorial-photo-input]");
    var photoFilename = form.querySelector("[data-bpf-memorial-photo-filename]");
    var photoError = form.querySelector("[data-bpf-memorial-photo-error]");
    var successEl = section ? section.querySelector("[data-bpf-memorial-success]") : null;
    var introEl = section ? section.querySelector("[data-bpf-memorial-intro]") : null;
    var defaultSubmitLabel = submitLabelEl ? submitLabelEl.textContent : "Submit";

    if (photoInput) {
      photoInput.addEventListener("change", function () {
        handlePhotoChange(photoInput, photoFilename, photoError);
      });
    }

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      submitForm(form, {
        submitButton: submitButton,
        submitLabelEl: submitLabelEl,
        defaultSubmitLabel: defaultSubmitLabel,
        formError: formError,
        successEl: successEl,
        introEl: introEl,
      });
    });
  }

  function handlePhotoChange(photoInput, photoFilename, photoError) {
    clearError(photoError);
    var file = photoInput.files && photoInput.files[0];

    if (!file) {
      setFilename(photoFilename, "No photo chosen");
      return;
    }

    if (ACCEPTED_PHOTO_TYPES.indexOf(file.type) === -1) {
      photoInput.value = "";
      setFilename(photoFilename, "No photo chosen");
      showError(photoError, "Please choose a JPEG, PNG, or WebP photo.");
      return;
    }

    if (file.size > MAX_PHOTO_BYTES) {
      photoInput.value = "";
      setFilename(photoFilename, "No photo chosen");
      showError(photoError, "Please choose a photo under 10 MB.");
      return;
    }

    setFilename(photoFilename, file.name);
  }

  function submitForm(form, ui) {
    // Duplicate-click / double-submit guard — the backend intentionally
    // has no idempotency key yet, so this is the only thing preventing a
    // second identical Memorial from being created by an eager click.
    if (form.dataset.submitting === "true") {
      return;
    }
    form.dataset.submitting = "true";

    clearError(ui.formError);
    setSubmitting(ui.submitButton, ui.submitLabelEl, true, ui.defaultSubmitLabel);

    var formData = new FormData(form);

    fetch(form.getAttribute("action"), {
      method: "POST",
      body: formData,
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            return { status: response.status, body: body };
          });
      })
      .then(function (result) {
        if (result.status === 201) {
          showSuccess(form, ui.successEl, ui.introEl);
          return;
        }
        showError(ui.formError, messageForStatus(result.status));
        form.dataset.submitting = "false";
        setSubmitting(ui.submitButton, ui.submitLabelEl, false, ui.defaultSubmitLabel);
      })
      .catch(function () {
        showError(ui.formError, "We couldn't submit your tribute right now. Please try again shortly.");
        form.dataset.submitting = "false";
        setSubmitting(ui.submitButton, ui.submitLabelEl, false, ui.defaultSubmitLabel);
      });
  }

  function messageForStatus(status) {
    switch (status) {
      case 400:
        return "Please check the information above and try again.";
      case 401:
        return "We couldn't submit your tribute right now. Please try again shortly.";
      case 413:
        return "Please choose a photo under 10 MB.";
      case 429:
        return "We've received several submissions from this connection. Please wait a little while and try again.";
      default:
        return "We couldn't submit your tribute right now. Please try again shortly.";
    }
  }

  function showSuccess(form, successEl, introEl) {
    // Collapse the form into the success state in place, rather than
    // resetting/blanking it, so the customer isn't left wondering whether
    // anything happened — and disabled inputs mean a second Enter/click
    // can't resubmit. The intro heading/body is a sibling of the form
    // (not inside it), so hiding the form alone never touched it — it
    // must be hidden explicitly here too, alongside the form, so the
    // success box is the only thing left visible in the section.
    form.hidden = true;
    disableAllFields(form);

    if (introEl) {
      introEl.hidden = true;
    }

    if (successEl) {
      successEl.hidden = false;
      successEl.focus();
    }
  }

  function disableAllFields(form) {
    var fields = form.querySelectorAll("input, textarea, button");
    fields.forEach(function (field) {
      field.disabled = true;
    });
  }

  function setSubmitting(submitButton, submitLabelEl, isSubmitting, defaultLabel) {
    if (!submitButton) return;
    submitButton.disabled = isSubmitting;
    if (submitLabelEl) {
      submitLabelEl.textContent = isSubmitting ? "Submitting…" : defaultLabel;
    }
  }

  function setFilename(photoFilename, text) {
    if (photoFilename) {
      photoFilename.textContent = text;
    }
  }

  function showError(errorEl, message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError(errorEl) {
    if (!errorEl) return;
    errorEl.textContent = "";
    errorEl.hidden = true;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }
})();
